import React, { useState } from 'react';
import { HugeiconsIcon } from "@hugeicons/react";
import { RefreshIcon, UserIcon, Shield01Icon, AlertIcon, MailIcon, AiPhoneIcon, ClockIcon } from "@hugeicons/core-free-icons";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SortOption } from "../components/search/searchUtils";

export default function RefundPolicy() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('name-asc');
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <Header 
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterMenuOpen={filterMenuOpen}
        setFilterMenuOpen={setFilterMenuOpen}
        sortMenuOpen={sortMenuOpen}
        setSortMenuOpen={setSortMenuOpen}
      />
      
      {/* Hero Section */}
      <section className="w-full bg-white">
        <div className="container py-0">
          <div className="text-center pt-16 pb-10 md:pt-[120px] md:pb-24">
            <div className="mb-4">
              <h1 className="text-4xl text-[var(--foreground)]">Refund Policy</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="w-full py-10 md:py-24 bg-gray-50">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-8">
            
            {/* Last Updated */}
            <div className="mb-8">
              <p className="bodysmall text-[var(--dispensary-gray-500)]">
                Last updated: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

            {/* General Policy */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                General Refund Policy
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  We strive to provide the highest quality products and services. If you are not completely 
                  satisfied with your purchase, we offer refunds and returns under the following conditions:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Returns must be initiated within 30 days of purchase</li>
                  <li>Products must be in original, unopened condition</li>
                  <li>Prescription medications are non-refundable for safety reasons</li>
                  <li>Shipping costs are non-refundable unless the error was on our part</li>
                </ul>
              </div>
            </div>

            {/* Prescription Medications */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Prescription Medications
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  Due to safety and regulatory requirements, prescription medications cannot be returned 
                  or refunded once dispensed. However, we will work with you in the following cases:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>If we made an error in dispensing the wrong medication</li>
                  <li>If the medication was damaged during shipping</li>
                  <li>If there was a pricing error on our part</li>
                  <li>If the medication is recalled by the manufacturer</li>
                </ul>
              </div>
            </div>

            {/* Over-the-Counter Products */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Over-the-Counter Products
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  Over-the-counter products can be returned within 30 days of purchase if:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>The product is in its original, unopened packaging</li>
                  <li>The product is not expired</li>
                  <li>The product was not used or damaged</li>
                  <li>The return is not for a product that has been recalled</li>
                </ul>
              </div>
            </div>

            {/* Return Process */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Return Process
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  To initiate a return, please follow these steps:
                </p>
                <ol className="list-decimal list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Contact our customer service within 30 days of purchase</li>
                  <li>Provide your order number and reason for return</li>
                  <li>Receive return authorization and shipping label</li>
                  <li>Package the item securely and ship it back</li>
                  <li>Wait for processing (3-5 business days)</li>
                  <li>Receive refund to original payment method</li>
                </ol>
              </div>
            </div>

            {/* Refund Timeline */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Refund Timeline
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  Once we receive your return, here's what to expect:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Processing time: 3-5 business days</li>
                  <li>Credit card refunds: 5-10 business days</li>
                  <li>Bank transfer refunds: 7-14 business days</li>
                  <li>Cash on delivery refunds: Processed via bank transfer</li>
                </ul>
              </div>
            </div>

            {/* Non-Refundable Items */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Non-Refundable Items
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  The following items are not eligible for refunds:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Prescription medications (except for dispensing errors)</li>
                  <li>Opened or used products</li>
                  <li>Expired products</li>
                  <li>Custom or personalized items</li>
                  <li>Digital products or services</li>
                  <li>Shipping and handling fees</li>
                </ul>
              </div>
            </div>

            {/* Damaged or Defective Items */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Damaged or Defective Items
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  If you receive a damaged or defective item:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Contact us within 48 hours of delivery</li>
                  <li>Take photos of the damage for documentation</li>
                  <li>Do not use the damaged product</li>
                  <li>We will arrange for replacement or refund</li>
                  <li>We cover return shipping for damaged items</li>
                </ul>
              </div>
            </div>

            {/* Cancellation Policy */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Order Cancellation
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  You may cancel your order under the following conditions:
                </p>
                <ul className="list-disc list-inside space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)] ml-4">
                  <li>Before the order is processed and shipped</li>
                  <li>If there are issues with prescription verification</li>
                  <li>If the product becomes unavailable</li>
                  <li>If there are pricing errors on our website</li>
                </ul>
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  Once an order is shipped, it cannot be cancelled and must go through the return process.
                </p>
              </div>
            </div>

            {/* Contact Us */}
            <div className="md:mb-8">
              <h4 className="h4 mb-2">
                Contact Us
              </h4>
              <div className="space-y-4">
                <p className="bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  If you have any questions about our refund policy or need to initiate a return, 
                  please contact us:
                </p>
                <div className="space-y-2 bodysmall md:bodymedium text-[var(--dispensary-gray-600)]">
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={MailIcon} size={16} />
                    <span>Email: returns@pharmacy.com</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={AiPhoneIcon} size={16} />
                    <span>Phone: +234 901 234 5678</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HugeiconsIcon icon={ClockIcon} size={16} />
                    <span>Hours: Monday - Friday, 8:00 AM - 6:00 PM</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 